import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { adminClient, type AdminIntegrationVisibilityRow, type AdminOrganizationRow } from '@/api/adminClient';
import { organizationName } from '@/components/org/OrganizationPicker';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { SearchInput } from '@/components/table/SearchInput';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import {
  VISIBILITY_FILTERS,
  type VisibilityFilter,
  filterIntegrationRows,
  integrationKindLabel,
  integrationPath,
  visibilitySummary,
} from '@/lib/integrationVisibility';

import { errorMessage } from '../IntegrationBuilder/builderModel';
import { IntegrationLogo } from '../IntegrationBuilder/builderUi';

const COLUMNS = [{ label: 'Integration' }, { label: 'Kind', className: 'hidden md:table-cell' }, { label: 'Who can use it', className: 'text-right' }];

/**
 * Studio Admin → Integrations: every catalog integration and Integration Builder connector with who can
 * use it. A row opens the integration, where an admin changes it.
 */
export function IntegrationsPage() {
  const [rows, setRows] = useState<AdminIntegrationVisibilityRow[]>();
  const [organizations, setOrganizations] = useState<AdminOrganizationRow[]>([]);
  const [error, setError] = useState<string>();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<VisibilityFilter>('all');

  const load = useCallback(async () => {
    setError(undefined);
    try {
      const [list, directory] = await Promise.all([
        adminClient.listIntegrationVisibility(),
        adminClient.listOrganizations().catch(() => ({ items: [] as AdminOrganizationRow[] })),
      ]);
      setRows(list.items);
      setOrganizations(directory.items);
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => filterIntegrationRows(rows ?? [], query, filter), [rows, query, filter]);
  const counts = useMemo(
    () =>
      Object.fromEntries(VISIBILITY_FILTERS.map((option) => [option.id, filterIntegrationRows(rows ?? [], '', option.id).length])) as Record<VisibilityFilter, number>,
    [rows],
  );

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Integrations"
        description="Who can use each integration: every organization, or only the ones you assign it to. Where an organization can use one, it is on unless they turn it off."
      />

      <DataTable
        toolbar={
          <>
            <SearchInput value={query} onChange={setQuery} placeholder="Search name or integration id" label="Search integrations" />
            <SegmentedFilter<VisibilityFilter>
              label="Who can use it"
              value={filter}
              onChange={setFilter}
              options={VISIBILITY_FILTERS.map((option) => ({ ...option, count: rows ? counts[option.id] : undefined }))}
            />
          </>
        }
      >
        <DataTableHead columns={COLUMNS} />
        {error ? (
          <DataTableLoadError colSpan={COLUMNS.length} message={error} onRetry={() => void load()} />
        ) : !rows ? (
          <DataTableMessage colSpan={COLUMNS.length} loading>
            Loading integrations
          </DataTableMessage>
        ) : visible.length === 0 ? (
          <DataTableMessage colSpan={COLUMNS.length}>{query.trim() ? `No integrations match “${query.trim()}”` : 'No integrations here'}</DataTableMessage>
        ) : (
          <tbody>
            {visible.map((row) => {
              const to = integrationPath(row.integrationId);
              const summary = visibilitySummary(row, (organizationId) => organizationName(organizations, organizationId));
              return (
                <DataTableRow key={row.integrationId} to={to}>
                  <DataTableCell className="max-w-md">
                    <Link to={to} className="flex min-w-0 items-center gap-3">
                      <IntegrationLogo src={row.logoUrl} color={row.brandColor ?? '#64748b'} title={row.title} size="sm" />
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{row.title}</span>
                        <span className="block truncate font-mono text-[11px] text-muted-foreground">{row.integrationId}</span>
                      </span>
                    </Link>
                  </DataTableCell>
                  <DataTableCell className="hidden text-xs text-muted-foreground md:table-cell">{integrationKindLabel(row.kind)}</DataTableCell>
                  <DataTableCell className="text-right">
                    <StatusPill tone={summary.tone}>{summary.label}</StatusPill>
                  </DataTableCell>
                </DataTableRow>
              );
            })}
          </tbody>
        )}
      </DataTable>
    </div>
  );
}
